/**
 * Chrome (desktop and Android) ships without Albanian date and number data: asked for
 * 'sq' it answers "Monday", "Sep" and "50,000". Components that rely on the browser's
 * Intl (the calendar's labels, the date picker) would then show English to most users.
 *
 * This loads the FormatJS polyfills with Albanian data, only in browsers that lack it
 * (Safari and Firefox have it and download nothing extra). The order matters: each
 * polyfill depends on the previous ones.
 */
export async function ensureAlbanianIntl(): Promise<void> {
  // The user's real time zone, read from the native Intl before any polyfill replaces it.
  const nativeTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    if ((await import('@formatjs/intl-getcanonicallocales/should-polyfill.js')).shouldPolyfill()) {
      await import('@formatjs/intl-getcanonicallocales/polyfill.js');
    }
    if ((await import('@formatjs/intl-locale/should-polyfill.js')).shouldPolyfill()) {
      await import('@formatjs/intl-locale/polyfill.js');
    }
    if ((await import('@formatjs/intl-pluralrules/should-polyfill.js')).shouldPolyfill('sq')) {
      await import('@formatjs/intl-pluralrules/polyfill-force.js');
      await import('@formatjs/intl-pluralrules/locale-data/sq.js');
    }
    if ((await import('@formatjs/intl-numberformat/should-polyfill.js')).shouldPolyfill('sq')) {
      await import('@formatjs/intl-numberformat/polyfill-force.js');
      await import('@formatjs/intl-numberformat/locale-data/sq.js');
    }
    if ((await import('@formatjs/intl-datetimeformat/should-polyfill.js')).shouldPolyfill('sq')) {
      await import('@formatjs/intl-datetimeformat/polyfill-force.js');
      await import('@formatjs/intl-datetimeformat/locale-data/sq.js');
      // Time-zone data (includes Europe/Tirane); needed by the polyfilled DateTimeFormat.
      await import('@formatjs/intl-datetimeformat/add-golden-tz.js');
      // Without this the polyfill formats in UTC (10:00 in Tirana would show as 08:00).
      (
        Intl.DateTimeFormat as unknown as { __setDefaultTimeZone?: (tz: string) => void }
      ).__setDefaultTimeZone?.(nativeTimeZone || 'Europe/Tirane');
    }
  } catch (err) {
    // Never block the app: without the polyfill some labels may fall back to English.
    console.error('Albanian Intl polyfill failed', err);
  }
}
